/**
 * loginHS 공개 API v1 문서 — /?page=api-docs (로그인 무관)
 * 내부 구현(엔진/서버/DB) 언급 금지. 키는 플레이스홀더(YOUR_API_KEY)만.
 * ※ 실제 구현과 1:1 정합 유지 — 다른 화면·연동사가 이 문서만 보고 붙일 수 있어야 한다. 안 되면 문서가 틀린 것(2026-10-06 보완:
 *   키별 한도·429·회사별 키·비동기 jobs·실제 응답 모양(결과에 status 칸 없음, 실패=error)).
 */
import React, { useEffect, useState } from "react";
import ApiPortalLayout, { SideTocItem } from "./ApiPortalLayout";

const S = {
  page: {
    maxWidth: 880,
    color: "#1f2937", // 셸(ApiPortalLayout)이 프레임 제공 — 내부 폭만 제한

    fontFamily: "'Inter','Malgun Gothic', '맑은 고딕', 'Pretendard',sans-serif",
    lineHeight: 1.65,
  },
  h1: { fontSize: 30, fontWeight: 800, marginBottom: 6 },
  h2: {
    fontSize: 20,
    fontWeight: 700,
    margin: "36px 0 10px",
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 6,
    scrollMarginTop: 72,
  },
  h3: { fontSize: 15, fontWeight: 700, margin: "16px 0 6px" },
  p: { fontSize: 14, color: "#4b5563", margin: "6px 0" },
  ol: { fontSize: 14, color: "#374151", margin: "6px 0 6px 18px", padding: 0 },
  pre: {
    background: "#0f172a",
    color: "#e2e8f0",
    padding: 14,
    borderRadius: "0 0 8px 8px",
    fontSize: 12.5,
    overflowX: "auto",
    whiteSpace: "pre",
    margin: 0,
  },
  preSolo: {
    background: "#0f172a",
    color: "#e2e8f0",
    padding: 14,
    borderRadius: 8,
    fontSize: 12.5,
    overflowX: "auto",
    whiteSpace: "pre",
  },
  tabbar: {
    display: "flex",
    gap: 2,
    flexWrap: "wrap",
    background: "#1e293b",
    borderRadius: "8px 8px 0 0",
    padding: "6px 6px 0",
  },
  tab: {
    background: "transparent",
    color: "#94a3b8",
    border: "none",
    borderRadius: "6px 6px 0 0",
    padding: "7px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  tabActive: { background: "#0f172a", color: "#e2e8f0" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13.5,
    margin: "8px 0",
  },
  th: {
    textAlign: "left",
    padding: "7px 10px",
    borderBottom: "2px solid #e5e7eb",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "7px 10px",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "top",
  },
  code: {
    background: "#f3f4f6",
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 12.5,
    fontFamily: "ui-monospace,monospace",
  },
  btn: {
    background: "#111827",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  note: {
    background: "#f8fafc",
    borderLeft: "3px solid #94a3b8",
    padding: "10px 14px",
    fontSize: 13,
    color: "#475569",
    borderRadius: 4,
    margin: "10px 0",
  },
};

function T({ head, rows }) {
  return (
    <table style={S.table}>
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} style={S.th}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j} style={S.td}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// 언어별 코드 탭 — EXAMPLES 배열에 항목만 추가하면 탭이 늘어난다.
function LangTabs({ examples }) {
  const [active, setActive] = useState(examples[0].key);
  const cur = examples.find((e) => e.key === active) || examples[0];
  return (
    <div>
      <div style={S.tabbar}>
        {examples.map((e) => (
          <button
            key={e.key}
            onClick={() => setActive(e.key)}
            style={{ ...S.tab, ...(active === e.key ? S.tabActive : {}) }}
          >
            {e.label}
          </button>
        ))}
      </div>
      <pre style={S.pre}>{cur.code}</pre>
    </div>
  );
}

const TOC = [
  ["#sec-1", "개요"],
  ["#sec-2", "인증 · 키 발급"],
  ["#sec-quickstart", "개발자 빠른 시작"],
  ["#sec-3", "요청"],
  ["#sec-4", "요청 예제"],
  ["#sec-5", "응답"],
  ["#sec-limits", "한도 · 429 처리"],
  ["#sec-jobs", "대량(비동기) 처리"],
  ["#sec-6", "에러 응답"],
  ["#sec-7", "버전 · 문의"],
];

// 개발자 연동 예제 — 실제 계약(X-Api-Key · 50건/요청 · 키별 분당·하루 한도 · 429 재시도 · results[]) 기준.
const JAVA_EXAMPLE = `// 백엔드(Java 11+, 서버-투-서버) — 브라우저에서 직접 호출 금지(CORS 미지원)
// HttpClient는 JDK 내장. JSON은 Jackson(com.fasterxml.jackson.databind) 사용.
import com.fasterxml.jackson.databind.*;
import com.fasterxml.jackson.databind.node.*;
import java.net.URI;
import java.net.http.*;
import java.util.*;

public class LoginHsClassifier {
  static final String ENDPOINT = "https://loginhs.tlogin.net/api/v1/classify/bulk";
  static final int CHUNK = 50;               // 요청당 최대 50건
  static final HttpClient HTTP = HttpClient.newHttpClient();
  static final ObjectMapper OM = new ObjectMapper();

  /** apiKey = 그 회사(사무소)의 키. items = {id, trade_name, specs[], components[], ...} 또는 {id, keyword}. */
  static List<JsonNode> classify(String apiKey, List<ObjectNode> items) throws Exception {
    List<JsonNode> out = new ArrayList<>();
    for (int i = 0; i < items.size(); i += CHUNK) {
      ObjectNode payload = OM.createObjectNode();
      payload.putArray("items").addAll(items.subList(i, Math.min(i + CHUNK, items.size())));
      HttpRequest req = HttpRequest.newBuilder(URI.create(ENDPOINT))
          .header("Content-Type", "application/json").header("X-Api-Key", apiKey)
          .POST(HttpRequest.BodyPublishers.ofString(OM.writeValueAsString(payload))).build();
      HttpResponse<String> res = null;
      for (int attempt = 0; attempt < 4; attempt++) {
        res = HTTP.send(req, HttpResponse.BodyHandlers.ofString());
        if (res.statusCode() != 429) break;
        JsonNode err = OM.readTree(res.body());
        if (err.path("message").asText().startsWith("Daily")) throw new RuntimeException("오늘 한도 초과");
        Thread.sleep(1000L * Math.max(1, err.path("retryAfterSeconds").asInt(10)));   // 분당 한도 — 기다렸다 같은 묶음 다시
      }
      if (res.statusCode() != 200) throw new RuntimeException("classify HTTP " + res.statusCode() + " " + res.body());
      OM.readTree(res.body()).get("results").forEach(out::add);   // {requestId, version, results, summary}
    }
    return out;
  }

  public static void main(String[] a) throws Exception {
    ObjectNode it = OM.createObjectNode().put("id", "line-1").put("trade_name", "PAPER BAGS");
    it.putArray("specs").add("518775-00").add("TIF WATCH BG PPR");
    for (JsonNode r : classify(System.getenv("LOGINHS_API_KEY"), List.of(it))) {
      if (!r.path("error").isNull()) System.out.println("실패: " + r.get("id").asText() + " " + r.get("error").asText());
      else System.out.println(r.get("id").asText() + " -> " + r.get("hsk").asText() + " (제안 · 신고 전 확인)");
    }
  }
}`;

const NODE_EXAMPLE = `// Node 18+ (서버-투-서버) — 브라우저에서 직접 호출 금지(CORS 미지원)
const ENDPOINT = "https://loginhs.tlogin.net/api/v1/classify/bulk";
const CHUNK = 50;                               // 요청당 최대 50건
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// apiKey = 그 회사(사무소)의 키. items = [{ id, trade_name, specs, components, ... }] 또는 [{ id, keyword }]
async function classify(apiKey, items) {
  const out = [];
  for (let i = 0; i < items.length; i += CHUNK) {
    const body = JSON.stringify({ items: items.slice(i, i + CHUNK) });
    let res;
    for (let attempt = 0; attempt < 4; attempt++) {
      res = await fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json", "X-Api-Key": apiKey }, body });
      if (res.status !== 429) break;
      const err = await res.json();
      if (String(err.message).startsWith("Daily")) throw new Error("오늘 한도 초과");
      await sleep(1000 * Math.max(1, err.retryAfterSeconds || 10));   // 분당 한도 — 기다렸다 같은 묶음 다시
    }
    if (!res.ok) throw new Error("classify HTTP " + res.status + " " + (await res.text()));
    out.push(...(await res.json()).results);     // { requestId, version, results, summary }
  }
  return out;
}

// 사용
const rows = await classify(process.env.LOGINHS_API_KEY, [
  { id: "line-1", trade_name: "PAPER BAGS", specs: ["518775-00", "TIF WATCH BG PPR"] },
]);
for (const r of rows) {
  if (r.error) console.log("실패:", r.id, r.error);
  else console.log(r.id, "->", r.hsk, "(제안 · 신고 전 확인)");
}`;

const PY_EXAMPLE = `# Python 3 (서버-투-서버) — 브라우저에서 직접 호출 금지(CORS 미지원)
import os, time, requests

ENDPOINT = "https://loginhs.tlogin.net/api/v1/classify/bulk"
CHUNK = 50                                   # 요청당 최대 50건

def classify(api_key, items):
    """api_key = 그 회사(사무소)의 키. items = [{id, trade_name, specs, components, ...}] 또는 [{id, keyword}]"""
    out = []
    for i in range(0, len(items), CHUNK):
        for attempt in range(4):
            r = requests.post(ENDPOINT, headers={"X-Api-Key": api_key},
                              json={"items": items[i:i + CHUNK]}, timeout=180)
            if r.status_code != 429:
                break
            err = r.json()
            if str(err.get("message", "")).startswith("Daily"):
                raise RuntimeError("오늘 한도 초과")
            time.sleep(max(1, err.get("retryAfterSeconds") or 10))   # 분당 한도 — 기다렸다 같은 묶음 다시
        r.raise_for_status()                 # 401/400/500 -> 예외
        out.extend(r.json()["results"])      # {requestId, version, results, summary}
    return out

# 사용
for row in classify(os.environ["LOGINHS_API_KEY"],
                    [{"id": "line-1", "trade_name": "PAPER BAGS", "specs": ["518775-00", "TIF WATCH BG PPR"]}]):
    if row["error"]:
        print("실패:", row["id"], row["error"])
    else:
        print(row["id"], "->", row["hsk"], "(제안 · 신고 전 확인)")`;

// 탭 순서 = 표시 순서. 언어 추가 시 여기에 { key, label, code } 만 넣으면 됨.
const CODE_EXAMPLES = [
  { key: "java", label: "Java (메인)", code: JAVA_EXAMPLE },
  { key: "node", label: "Node.js", code: NODE_EXAMPLE },
  { key: "python", label: "Python", code: PY_EXAMPLE },
];

export default function ApiDocsPage({ onNavigate }) {
  const C = (t) => <code style={S.code}>{t}</code>;
  // 딥링크(#sec-N) — 가이드 Q&A 인용 칩 등 외부 진입 시 해당 섹션으로 스크롤
  useEffect(() => {
    const h = window.location.hash;
    if (h) {
      const el = document.querySelector(h);
      if (el) setTimeout(() => el.scrollIntoView({ block: "start" }), 50);
    }
  }, []);
  return (
    <ApiPortalLayout
      active="docs"
      onNavigate={onNavigate}
      toc={TOC.map(([href, label]) => (
        <SideTocItem key={href} href={href}>
          {label}
        </SideTocItem>
      ))}
    >
      <div style={S.page}>
        <h1 style={S.h1}>loginHS 세번분류 API v1</h1>
        <p style={S.p}>
          여러 품명을 한 번의 요청으로 HS 세번(HSK 10자리)까지 분류합니다. 확신
          건은 자동 확정, 애매한 건은 <b>검토 필요(review_needed)</b> 플래그와
          후보 목록으로 반환되어 사람 검토 큐에 연결할 수 있습니다.
        </p>

        <h2 id="sec-1" style={S.h2}>
          개요
        </h2>
        <T
          head={["항목", "내용"]}
          rows={[
            [
              "기능",
              "품명·신고서 칸 목록 일괄 HS 세번분류 — 학습 모델(PROVE)의 10자리 제안. 확정이 아니므로 신고 전 확인",
            ],
            [
              "Method / URL",
              <span>
                {C("POST")}{" "}
                {C("https://loginhs.tlogin.net/api/v1/classify/bulk")}
              </span>,
            ],
            ["Content-Type", C("application/json")],
            ["인증", <span>{C("X-Api-Key")} 헤더 (API 키 필요)</span>],
            ["호출 방식", "서버-투-서버 (브라우저 직접 호출용 CORS 미지원)"],
            ["대량(비동기)", <span>{C("POST /api/v1/classify/jobs")} → {C("GET /api/v1/classify/jobs/{jobId}")} (아래 대량 처리)</span>],
          ]}
        />

        <h2 id="sec-2" style={S.h2}>
          인증 · 키 발급
        </h2>
        <pre style={S.preSolo}>{`X-Api-Key: YOUR_API_KEY`}</pre>
        <p style={S.p}>
          키가 없거나 유효하지 않으면 {C("401 Unauthorized")} 가 반환됩니다.
          키는 로그인 후 아래에서 무료로 발급할 수 있습니다(베타). 발급 순간
          1회만 표시되니 안전하게 보관하세요. 키는 {C("lhs_")} 로 시작합니다.
        </p>
        <div style={S.note}>
          <b>키는 회사(사무소)마다 따로</b> 씁니다 — 사용량·한도·요금이 키별로 집계됩니다.
          여러 회사를 대신 호출하는 연동(예: 통관 프로그램)은 회사별 키를 받아, <b>요청한 사용자의 회사 키로</b> 보내세요.
          그 회사 키가 없으면 다른 회사 키로 대신 보내지 마세요(사용량이 섞입니다). 키는 서버 설정에만 두고
          화면·메신저·저장소에 남기지 마세요. 잃어버리면 폐기하고 다시 발급합니다.
        </div>
        <button style={S.btn} onClick={() => onNavigate("api-keys")}>
          API 키 발급 →
        </button>
        <span style={{ marginLeft: 12 }}>
          <a
            href="/?page=contact"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("contact");
            }}
          >
            발급/연동 문의
          </a>
        </span>

        <h2 id="sec-quickstart" style={S.h2}>
          개발자 빠른 시작
        </h2>
        <p style={S.p}>
          연동 담당 개발자는 아래 3단계만 하면 됩니다. (호출은 반드시{" "}
          <b>여러분의 백엔드 서버에서</b> — 브라우저에서 직접 호출하면 CORS 로
          막힙니다.)
        </p>
        <ol style={S.ol}>
          <li>
            <b>키 발급</b> — 위 <b>[API 키 발급]</b> 버튼 → 로그인 →{" "}
            {C("lhs_…")} 키를 복사해 서버 환경변수(예 {C("LOGINHS_API_KEY")})에
            저장.
          </li>
          <li>
            <b>백엔드에서 호출</b> — 아래 예제(탭에서 언어 선택)를 복사해
            붙여넣고 실행. 50건씩 나눠 호출하고, 한도에 걸리면(429) 기다렸다 같은 묶음을 다시 보냅니다.
          </li>
          <li>
            <b>결과 사용</b> — {C("results[].error")} 가 비어 있으면 {C("results[].hsk")}(10자리 세번 제안)를 쓰고,
            있으면 그 줄만 실패로 표시. 제안은 확정이 아니므로 신고 전 사람이 확인합니다.
          </li>
        </ol>
        <LangTabs examples={CODE_EXAMPLES} />
        <div style={S.note}>
          막히거나 궁금한 점은 <b>디스코드</b> 로 물어보세요. (초대 링크는 준비
          중 — 우선{" "}
          <a
            href="/?page=contact"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("contact");
            }}
          >
            연동 문의
          </a>{" "}
          로 남겨주시면 담당자가 회신합니다.)
        </div>

        <h2 id="sec-3" style={S.h2}>
          요청
        </h2>
        <T
          head={["파라미터", "타입", "필수", "설명"]}
          rows={[
            [C("items"), "Array", "필수", "분류할 품목 목록 (1~50개)"],
            [
              C("items[].id"),
              "String",
              "선택",
              "라인 식별자 — 응답에 그대로 echo (생략 시 배열 순번)",
            ],
            [
              C("items[].keyword"),
              "String",
              "필수*",
              "품명 한 줄. 가진 정보(거래품명·규격·성분 등)를 띄어쓰기로 이어 붙여 넣어도 됩니다. *trade_name 이나 specs 를 보내면 생략 가능",
            ],
            [C("items[].trade_name"), "String", "선택", "거래품명 (최대 200자)"],
            [C("items[].specs"), "String[]", "선택", "규격1~10을 순서대로 (최대 10칸, 빈 칸은 빈 문자열)"],
            [C("items[].components"), "String[]", "선택", "성분1~3 (최대 3칸)"],
            [C("items[].partner"), "String", "선택", "무역거래처(해외 공급자·구매자) 상호"],
            [C("items[].partner_country / origin"), "String", "선택", "무역거래처 나라 · 원산지 (ISO 2자리)"],
            [C("items[].model / part_no"), "String", "선택", "모델·상표 · Part No"],
            [C("items[].direction"), "String", "선택", '"import" · "export"'],
            [
              C("items[].facts"),
              "Object",
              "선택",
              "결정트리가 요청한 확인값. 의류 construction·garment_type, 신발 upper_material·outer_sole_material, 기계 item_role·machine_family·self_propelled_status 등의 정규값을 전달",
            ],
            [
              C("options.decision_tree_shadow"),
              "Boolean",
              "선택",
              "독립 상위 결정트리를 명시적으로 실행. facts가 있거나 지원 류 신호가 있으면 자동으로도 실행",
            ],
            [
              C("options.apparel_tree_shadow"),
              "Boolean",
              "선택",
              "제61·62류 초기 연동 호환 옵션",
            ],
            [
              C("options.prove"),
              "Boolean",
              "선택",
              "PROVE 모델 제안(prove_hsk) 여부 — 기본 true",
            ],
          ]}
        />
        <h2 id="sec-limits" style={S.h2}>
          한도 · 429 처리
        </h2>
        <T
          head={["제한", "값", "넘으면"]}
          rows={[
            ["요청당 최대 항목", "50건", <span>{C("400 BAD_REQUEST")} — 50건씩 나눠 보내기</span>],
            ["분당 요청 수", "키별 (기본 10회, 계약에 따라 조정)", <span>{C("429")} · {C("Rate limit exceeded (requests/min).")} · {C("retryAfterSeconds")}</span>],
            ["하루 처리 항목", "키별 (기본 1,000건, 계약에 따라 조정, 서버 날짜 기준 초기화)", <span>{C("429")} · {C("Daily item quota exceeded.")} · {C("retryAfterSeconds")}=초기화까지 남은 초</span>],
            ["비동기 jobs", "요청당 최대 500건 · 키당 동시 2개", <span>{C("429")} · {C("Active jobs limit")}</span>],
            ["처리 시간", "50건 한 번에 수 초", "—"],
          ]}
        />
        <div style={S.note}>
          <b>429 를 받으면 멈추지 말고</b> 응답의 {C("retryAfterSeconds")} 만큼 기다린 뒤 <b>같은 묶음</b>을 다시 보내세요(최대 3번).
          메시지가 {C("Daily item quota exceeded.")} 이면 다시 보내지 말고 "오늘 한도 초과"로 안내합니다.
          한 묶음이 실패해도 이미 받은 묶음의 결과는 화면에 채우세요. 한도는 키(회사)마다 정해지므로 늘리려면 문의해 주세요.
        </div>
        <pre style={S.preSolo}>{`// 429 응답 본문
{ "status": 429, "success": false, "errorCode": "RATE_LIMITED",
  "message": "Rate limit exceeded (requests/min).", "requestId": "…", "retryAfterSeconds": 23 }`}</pre>

        <h2 id="sec-4" style={S.h2}>
          요청 예제
        </h2>
        <pre
          style={S.preSolo}
        >{`curl -X POST https://loginhs.tlogin.net/api/v1/classify/bulk \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -d '{
    "items": [
      { "id": "line-1", "keyword": "가죽 가방" },
      { "id": "line-2", "keyword": "잠수복" },
      { "id": "line-3", "keyword": "DC 밧데리 충전기 자동자료처리기계용" },
      { "id": "apparel-1", "keyword": "셔츠", "facts": {
        "construction": "woven", "garment_type": "shirt",
        "garment_gender": "men", "textile_material": "cotton"
      } },
      { "id": "footwear-1", "keyword": "가죽 갑피 운동화", "facts": {
        "item_kind": "footwear", "upper_material": "leather",
        "outer_sole_material": "rubber_plastics"
      } },
      { "id": "machinery-1", "keyword": "AERIAL LIFT SJ3-3219", "facts": {
        "item_kind": "machinery", "item_role": "complete_machine",
        "machine_family": "self_propelled_elevating_work_platform",
        "work_platform_status": true, "lifting_or_handling_equipment_status": true,
        "self_propelled_status": true, "travel_drive_electric_status": true
      } }
    ],
    "options": { "decision_tree_shadow": true }
  }'`}</pre>
        <pre style={S.preSolo}>{`POST /api/v1/classify/bulk HTTP/1.1
Host: loginhs.tlogin.net
X-Api-Key: YOUR_API_KEY
Content-Type: application/json

{ "items": [ { "id": "line-1", "keyword": "가죽 가방" } ] }`}</pre>
        <p style={S.p}>
          신고서 칸을 가지고 있다면 칸 그대로 보내 주세요. 모든 칸은 선택이며 있는 만큼만 보내면 됩니다.
          칸 이름을 모르면 가진 정보를 띄어쓰기로 이어 붙여 {C("keyword")} 한 칸에 넣어도 됩니다.
          <b>칸으로 보내면 정답률이 크게 오릅니다</b> — 실측에서 규격을 한 줄로 이어 붙여 {C("keyword")} 로만 보낸 경우보다
          거래품명·규격·성분을 칸대로 보낸 경우가 10자리 정답률이 두 배 넘게 높았습니다. 가진 칸은 모두 보내 주세요.
        </p>
        <pre style={S.preSolo}>{`{ "items": [ {
    "id": "lan-001-1",
    "trade_name": "PAPER BAGS",
    "specs": ["518775-00", "TIF WATCH BG PPR", "11X9.5X6.5"],
    "components": ["PAPER 100%"],
    "partner": "DSV SOLUTIONS PTE LTD", "partner_country": "SG",
    "origin": "CN", "direction": "import"
} ] }`}</pre>

        <h2 id="sec-5" style={S.h2}>
          응답
        </h2>
        <pre style={S.preSolo}>{`{
  "version": "v1",
  "requestId": "6f1c…",
  "results": [
    {
      "id": "line-1", "keyword": "가죽 가방",
      "hsk": "4202211090", "hs6": "420221", "hs4": "4202", "chapter": "42",
      "name_ko": null, "confidence": null, "score": null,
      "decision_label": "PROVE 모델 제안",
      "review_needed": true, "top_candidates": [],
      "prove_hsk": "4202211090", "prove_hs6": "420221", "prove_hs4": "4202",
      "error": null
    },
    {
      "id": "line-2", "keyword": "잠수복",
      "hsk": null, "hs6": null, "hs4": null, "chapter": null,
      "review_needed": true, "top_candidates": [],
      "prove_hsk": null, "error": "model_unavailable"
    },
    // ↓ options.decision_tree_shadow / subdivision_shadow 나 facts 를 보낸 요청에서만 붙는 비교용 칸
    {
      "id": "line-3", "keyword": "DC 밧데리 충전기 자동자료처리기계용",
      "hsk": "8504401010", "hs6": "850440", "confidence": "medium",
      "review_needed": true,
      "subdivision_shadow": {
        "shadow": true, "proposed_hsk": "8504401010",
        "proposed_path_supported": false,
        "supported_hsks": ["8504403010"], "supported_hsk": "8504403010",
        "conflict": true, "requires_review": true, "action": "review_conflict",
        "questions": []
      },
      "error": null
    },
    {
      "id": "apparel-1", "keyword": "셔츠",
      "decision_tree_shadow": {
        "shadow": true, "uses_legacy_as_input": false, "action": "supported",
        "supported_chapter": "62", "supported_hs4": "6205",
        "supported_hs6": "620520", "supported_hsk": "6205200000",
        "questions": []
      },
      "apparel_tree_shadow": {
        "shadow": true, "uses_legacy_as_input": false, "action": "supported",
        "supported_chapter": "62", "supported_hs4": "6205",
        "supported_hs6": "620520", "supported_hsk": "6205200000",
        "questions": []
      },
      "error": null
    },
    {
      "id": "footwear-1", "keyword": "가죽 갑피 운동화",
      "decision_tree_shadow": {
        "shadow": true, "uses_legacy_as_input": false, "action": "ask",
        "supported_chapter": "64", "supported_hs4": "6403",
        "questions": [ { "fact_key": "sports_footwear_status",
          "options": ["yes", "no"] } ]
      },
      "error": null
    },
    {
      "id": "machinery-1", "keyword": "AERIAL LIFT SJ3-3219",
      "decision_tree_shadow": {
        "shadow": true, "router_connected": true,
        "uses_legacy_as_input": false, "action": "ask",
        "supported_chapter": "84", "supported_hs4": "8427",
        "supported_hs6": "842710", "questions": []
      },
      "machinery_tree_shadow": {
        "shadow": true, "router_connected": true, "action": "ask",
        "supported_hs4": "8427", "supported_hs6": "842710"
      },
      "error": null
    }
  ],
  "summary": { "total": 6, "auto": 3, "review": 3, "failed": 0,
    "subdivision_conflicts": 1, "decision_tree_conflicts": 0,
    "apparel_tree_conflicts": 0, "footwear_tree_conflicts": 0,
    "machinery_tree_conflicts": 0, "elapsed_ms": 12400 }
}`}</pre>
        <T
          head={["필드", "타입", "설명"]}
          rows={[
            [C("version"), "String", 'API 버전 — 현재 "v1"'],
            [C("results[].id"), "String", "요청 items[].id echo (없으면 순번)"],
            [C("results[].error"), "String|null", <span><b>null 이면 성공.</b> 값이 있으면 그 줄만 실패 — {C("model_unavailable")}(잠시 뒤 다시) 등. 결과에 {C("status")} 칸은 없습니다</span>],
            [
              C("results[].hsk"),
              "String|null",
              "HSK 10자리 — 기본 응답에서는 학습 모델(PROVE)의 제안(= prove_hsk). 실패하면 null",
            ],
            [
              C("results[].hs6 / hs4 / chapter"),
              "String|null",
              "HS 6자리 / 4자리(호) / 류",
            ],
            [C("results[].name_ko"), "String|null", "결정 세번의 한글 품목명"],
            [C("results[].confidence / score"), "String|Number|null", "기본 응답에서는 null (비교용 옵션 요청에서만 값)"],
            [
              C("results[].decision_label"),
              "String|null",
              "결정 요약 라벨 (예: HIGH (즉시 결정) · 보류 …)",
            ],
            [
              C("results[].review_needed"),
              "Boolean",
              "기본 응답은 항상 true — 제안이지 확정이 아니므로 신고 전 확인",
            ],
            [C("results[].top_candidates"), "Array", "검토용 후보 세번 목록"],
            [
              C("results[].prove_hsk / prove_hs6 / prove_hs4"),
              "String|null",
              "PROVE 모델이 제안한 세번 — hsk 와 별개인 제안이며 확정이 아닙니다. 모델을 쓸 수 없을 때는 null",
            ],
            [
              C("results[].subdivision_shadow"),
              "Object|생략",
              "국내세분 결정표가 있는 HS6의 비교용 판정. 기존 hsk를 변경하지 않음",
            ],
            [
              C("results[].subdivision_shadow.supported_hsk"),
              "String|생략",
              "비차단 지지 잎이 정확히 하나일 때의 HSK10 비교 후보",
            ],
            [
              C("results[].subdivision_shadow.conflict"),
              "Boolean",
              "true = 엔진 제안과 결정표의 유일 지지 잎이 달라 수기 검토 필요",
            ],
            [
              C("results[].decision_tree_shadow"),
              "Object|생략",
              "제61·62·64류와 제84류 8425~8431의 류→HS4→HS6→HSK10 독립 결정트리. 기존 hsk는 입력으로 쓰지 않으며 계산 후 비교만 함",
            ],
            [
              C("results[].decision_tree_shadow.questions"),
              "Array",
              "다음 요청의 items[].facts로 답할 확인 축과 선택지",
            ],
            [
              C("results[].apparel_tree_shadow / footwear_tree_shadow / machinery_tree_shadow"),
              "Object|생략",
              "도메인별 호환·진단용 별칭. 공통 소비자는 decision_tree_shadow를 사용",
            ],
            [
              C("summary"),
              "Object",
              "total / auto / review / failed와 subdivision·decision_tree·apparel·footwear·machinery 충돌 수 / elapsed_ms",
            ],
          ]}
        />
        <div style={S.note}>
          개별 항목의 오류(빈 품명·모델 일시 불가 등)는 전체 요청을 실패시키지 않습니다 — 해당
          항목만 {C("hsk")} 가 null 이고 {C("error")} 에 사유가 담깁니다(그 외 항목은 정상 처리).
        </div>
        <div style={S.note}>
          {C("subdivision_shadow")}는 수기 비교용이며 {C("shadow:true")}입니다.
          {C("supported_hsk")}가 있어도 기존 {C("hsk")}를 자동으로 덮어쓰지 마세요.
          특히 {C("conflict:true")}는 정답 확정이 아니라 규격·용도 재확인 신호입니다.
        </div>
        <div style={S.note}>
          {C("decision_tree_shadow")}는 현재 제61·62·64류와 제84류 제8425호~제8431호에서 동작합니다. {C("action:ask")}
          또는 {C("ambiguous")}이면 {C("questions[].fact_key")}의 답을 같은 품목의
          {C("facts")}에 넣어 다시 호출하세요. 이 shadow도 기존 {C("hsk")}를 자동으로
          덮어쓰지 않습니다. 품명에 지원 류 신호가 있거나 {C("facts")}가 있으면 자동으로
          활성화됩니다. 제61·62류용 {C("apparel_tree_shadow")}, 제64류용
          {C("footwear_tree_shadow")}, 제84류용 {C("machinery_tree_shadow")}는 호환 별칭입니다.
        </div>

        <h2 id="sec-jobs" style={S.h2}>
          대량(비동기) 처리
        </h2>
        <p style={S.p}>
          수백 건을 한 번에 맡기고 결과를 나중에 받는 방법입니다. 요청 본문({C("items")}·{C("options")})은 위와 같고 최대 500건입니다.
        </p>
        <pre style={S.preSolo}>{`POST /api/v1/classify/jobs          → 202 { "requestId": "…", "jobId": "123", "status": "queued", "total": 272 }
GET  /api/v1/classify/jobs/123      → 200 { "jobId": "123", "status": "running", "progress": { "done": 120, "total": 272 } }
GET  /api/v1/classify/jobs/123      → 200 { "jobId": "123", "status": "done", "progress": {…}, "results": [ … ], "summary": { … } }
                                       (실패하면 "status": "failed", "error": "processing_failed")`}</pre>
        <div style={S.note}>
          같은 키로만 조회됩니다. 3~5초 간격으로 {C("status")} 가 {C("done")}·{C("failed")} 가 될 때까지 조회하세요.
          결과는 7일 뒤 지워집니다. 사용량(하루 한도)은 끝났을 때 건수만큼 셉니다.
        </div>

        <h2 id="sec-6" style={S.h2}>
          에러 응답
        </h2>
        <p style={S.p}>
          배치 전체가 거부되는 경우입니다(개별 항목 오류는 §5 참고). 상태 코드로
          구분하세요.
        </p>
        <pre style={S.preSolo}>{`// 모든 에러: { "status", "success": false, "errorCode", "message", "requestId"? }

// 401 — 키 누락 / 무효 / 폐기
{ "status": 401, "success": false, "errorCode": "INVALID_API_KEY", "message": "Invalid API Key." }

// 400 — items 누락·빈 배열 / 50건 초과
{ "status": 400, "success": false, "errorCode": "BAD_REQUEST", "message": "Too many items: 51 (max 50).", "requestId": "…" }

// 429 — 한도 초과(위 '한도 · 429 처리')
{ "status": 429, "success": false, "errorCode": "RATE_LIMITED", "message": "Daily item quota exceeded.", "requestId": "…", "retryAfterSeconds": 3600 }

// 404 — 없는 job(다른 키의 job 포함)
{ "status": 404, "success": false, "errorCode": "NOT_FOUND", "message": "Job not found." }

// 500 — 서버 오류(잠시 뒤 다시)
{ "status": 500, "success": false, "errorCode": "INTERNAL", "message": "Internal error.", "requestId": "…" }`}</pre>

        <h2 id="sec-7" style={S.h2}>
          버전 · 문의
        </h2>
        <p style={S.p}>
          본 API 는 <b>v1 계약 고정</b>입니다 — 하위 호환이 깨지는 변경은 v2 로
          제공합니다. 무료 베타 이후 유료 플랜 전환 시 사전 공지합니다.
        </p>
        <p style={S.p}>
          <a
            href="/?page=contact"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("contact");
            }}
          >
            연동 문의
          </a>
        </p>
      </div>
    </ApiPortalLayout>
  );
}
